import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle, AlertCircle, AlertTriangle, Info, X } from "lucide-react";

export type NotificationType = "success" | "error" | "warning" | "info";

export interface NotificationState {
  type: NotificationType;
  message: string;
}

interface InlineNotificationProps {
  notification: NotificationState | null;
  onDismiss: () => void;
  autoDismissMs?: number;
}

const config: Record<
  NotificationType,
  { icon: React.FC<any>; bg: string; border: string; text: string; iconColor: string }
> = {
  success: {
    icon: CheckCircle,
    bg: "bg-emerald-50",
    border: "border-emerald-200",
    text: "text-emerald-700",
    iconColor: "text-emerald-500",
  },
  error: {
    icon: AlertCircle,
    bg: "bg-red-50",
    border: "border-red-200",
    text: "text-red-700",
    iconColor: "text-red-500",
  },
  warning: {
    icon: AlertTriangle,
    bg: "bg-amber-50",
    border: "border-amber-200",
    text: "text-amber-700",
    iconColor: "text-amber-500",
  },
  info: {
    icon: Info,
    bg: "bg-blue-50",
    border: "border-blue-200",
    text: "text-blue-700",
    iconColor: "text-blue-500",
  },
};

const InlineNotification: React.FC<InlineNotificationProps> = ({
  notification,
  onDismiss,
  autoDismissMs = 5000,
}) => {
  useEffect(() => {
    if (!notification) return;
    const timer = setTimeout(onDismiss, autoDismissMs);
    return () => clearTimeout(timer);
  }, [notification, onDismiss, autoDismissMs]);

  return (
    <AnimatePresence>
      {notification && (
        <motion.div
          initial={{ opacity: 0, y: -8, height: 0 }}
          animate={{ opacity: 1, y: 0, height: "auto" }}
          exit={{ opacity: 0, y: -8, height: 0 }}
          transition={{ duration: 0.2 }}
          className="overflow-hidden"
        >
          {(() => {
            const c = config[notification.type];
            const Icon = c.icon;
            return (
              <div
                className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${c.bg} ${c.border}`}
              >
                <Icon className={`h-4 w-4 flex-shrink-0 ${c.iconColor}`} />
                <p className={`text-sm flex-1 ${c.text}`}>{notification.message}</p>
                <button
                  onClick={onDismiss}
                  className={`p-1 rounded-lg hover:bg-white/50 ${c.text} flex-shrink-0`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            );
          })()}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default InlineNotification;
